import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Database, 
  Users, 
  Bot, 
  BarChart3, 
  Facebook,
  TrendingUp,
  Activity,
  Mail,
  AlertTriangle,
  CheckCircle,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface DatabaseStats {
  users: {
    total: number;
    active: number;
    withPages: number;
    recentSignups: number;
  };
  pages: {
    total: number;
    active: number;
    categories: { [key: string]: number };
    averageFollowers: number;
  };
  messages: {
    total: number;
    unread: number;
    replied: number;
    aiResponses: number;
    responseRate: number;
  };
  analytics: {
    totalEvents: number;
    recentEvents: number;
    topEventTypes: { type: string; count: number }[];
    averageResponseTime: number;
  };
  aiPerformance: {
    totalResponses: number;
    successRate: number;
    averageResponseTime: number;
    topPrompts: string[];
  };
}

const DatabaseOverview: React.FC = () => {
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDatabaseStats();
  }, []);

  const fetchDatabaseStats = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/database/overview`);
      setStats(response.data);
      setLastUpdated(new Date());
    } catch (error: any) {
      console.error('Error fetching database stats:', error);
      setError('Failed to load database statistics');
      // No mock data - show error instead
      setStats(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatTime = (ms: number) => {
    if (ms >= 1000) return (ms / 1000).toFixed(1) + 's';
    return ms + 'ms';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading database overview...</span>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error || 'Failed to load database overview'}</p>
        <Button onClick={fetchDatabaseStats} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Database className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Database Overview</h1>
              <p className="text-gray-600">Complete system statistics and analytics</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </div>
            <Button onClick={fetchDatabaseStats} size="sm" variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* System Health */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-green-900">
              <CheckCircle className="h-5 w-5" />
              <span>System Health</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-900">98.7%</div>
                <div className="text-sm text-green-600">Uptime</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-900">{formatTime(stats.analytics.averageResponseTime)}</div>
                <div className="text-sm text-green-600">Avg Response Time</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-900">{stats.aiPerformance.successRate}%</div>
                <div className="text-sm text-green-600">AI Success Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-900">{formatNumber(stats.analytics.recentEvents)}</div>
                <div className="text-sm text-green-600">Events (24h)</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Main Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {/* Users Stats */}
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-blue-900">
              <Users className="h-5 w-5" />
              <span>Users</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-900">{formatNumber(stats.users.total)}</div>
              <div className="text-sm text-blue-600">Total Users</div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-700">Active:</span>
                <span className="font-medium text-blue-900">{stats.users.active}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">With Pages:</span>
                <span className="font-medium text-blue-900">{stats.users.withPages}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">New (7d):</span>
                <span className="font-medium text-blue-900">{stats.users.recentSignups}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pages Stats */}
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-purple-900">
              <Facebook className="h-5 w-5" />
              <span>Pages</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-900">{formatNumber(stats.pages.total)}</div>
              <div className="text-sm text-purple-600">Total Pages</div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-purple-700">Active:</span>
                <span className="font-medium text-purple-900">{stats.pages.active}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-purple-700">Avg Followers:</span>
                <span className="font-medium text-purple-900">{formatNumber(stats.pages.averageFollowers)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Messages Stats */}
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-green-900">
              <Mail className="h-5 w-5" />
              <span>Messages</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-900">{formatNumber(stats.messages.total)}</div>
              <div className="text-sm text-green-600">Total Messages</div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-green-700">Unread:</span>
                <span className="font-medium text-green-900">{stats.messages.unread}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">AI Responses:</span>
                <span className="font-medium text-green-900">{formatNumber(stats.messages.aiResponses)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Response Rate:</span>
                <span className="font-medium text-green-900">{stats.messages.responseRate}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Performance */}
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-orange-900">
              <Bot className="h-5 w-5" />
              <span>AI Performance</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-900">{formatNumber(stats.aiPerformance.totalResponses)}</div>
              <div className="text-sm text-orange-600">AI Responses</div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-orange-700">Success Rate:</span>
                <span className="font-medium text-orange-900">{stats.aiPerformance.successRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-700">Avg Time:</span>
                <span className="font-medium text-orange-900">{formatTime(stats.aiPerformance.averageResponseTime)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Detailed Analytics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        {/* Page Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <span>Page Categories</span>
            </CardTitle>
            <CardDescription>Distribution of Facebook pages by category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.pages.categories).map(([category, count]) => (
                <div key={category} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{category}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${(count / stats.pages.total) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Event Types */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="h-5 w-5" />
              <span>Top Event Types</span>
            </CardTitle>
            <CardDescription>Most common system events</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.analytics.topEventTypes.map((event, index) => (
                <div key={event.type} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Badge className="text-xs border-gray-300">
                      #{index + 1}
                    </Badge>
                    <span className="text-sm text-gray-700">{event.type.replace(/_/g, ' ')}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{formatNumber(event.count)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* AI Prompts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5" />
              <span>Popular AI Prompts</span>
            </CardTitle>
            <CardDescription>Most commonly used AI prompt templates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.aiPerformance.topPrompts.map((prompt, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-gray-900">#{index + 1}</div>
                  <div className="text-sm text-gray-600">{prompt}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default DatabaseOverview; 